import React from 'react';
import { FieldError, FieldValues, useController, UseControllerProps } from 'react-hook-form';
import {
  HelpText,
  Input,
  InputGroup as BuildersInputGroup,
  Label,
  ValidationMessage,
} from '@gjensidige/builders-components';

type InputGroupProps<T extends FieldValues> = {
  labelText: string;
  options: { label: string; value: string }[];
  helpText?: string;
  error: FieldError | undefined;
  type: 'checkbox' | 'radio';
} & UseControllerProps<T> &
  Omit<React.ComponentProps<typeof BuildersInputGroup>, 'onChange' | 'options'>;

const InputGroup = <T extends FieldValues>({
  name,
  control,
  labelText,
  options,
  helpText,
  error,
  type,
  ...props
}: InputGroupProps<T>) => {
  const { field } = useController({ name, control });
  const showValidationMessage = error !== undefined;

  const handleChange = (value: string[]) => {
    if (type === 'checkbox') {
      field.onChange(value.toString());
    } else {
      field.onChange(value[0]);
    }
  };

  return (
    <BuildersInputGroup name={name} onChange={(e) => handleChange(e)} type={type} {...props}>
      <Label>
        {labelText}
        {helpText !== undefined && <HelpText>{helpText}</HelpText>}
      </Label>

      {options.map((option, key) => {
        return (
          <Label key={key}>
            {option.label}
            <Input value={option.value} />
          </Label>
        );
      })}
      <ValidationMessage hidden={!showValidationMessage} invalid={showValidationMessage}>
        {error?.message}
      </ValidationMessage>
    </BuildersInputGroup>
  );
};

export default InputGroup;
